import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { useCustomers, usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import { apiFetch } from "@/utils/api";

interface CheckoutRow {
  id: string;
  checkout_number: string;
  checked_out_to: string;
  customer_name: string;
  due_back_date: string;
  status: string;
  created_at: string;
}

export function CheckoutPage() {
  const { checkoutId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = searchParams.get("new") === "1";
  const [checkouts, setCheckouts] = useState<CheckoutRow[]>([]);
  const [detail, setDetail] = useState<{
    checkout: Record<string, unknown>;
    lines: Record<string, unknown>[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { parts, loading: partsLoading } = usePartsAndLocations();
  const { customers, loading: custLoading } = useCustomers();

  const loadDetail = async (id: string) => {
    const data = await apiFetch<typeof detail>(`/equipment-checkouts/${id}`);
    setDetail(data);
  };

  useEffect(() => {
    (async () => {
      try {
        if (checkoutId && !isNew) {
          await loadDetail(checkoutId);
        } else if (!isNew) {
          const data = await apiFetch<{ checkouts: CheckoutRow[] }>("/equipment-checkouts");
          setCheckouts(data.checkouts);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [checkoutId, isNew]);

  const returnLine = async (lineId: string, quantity: number) => {
    if (!checkoutId) return;
    try {
      await apiFetch(`/equipment-checkouts/${checkoutId}/return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lineId, quantity }),
      });
      await loadDetail(checkoutId);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Return failed");
    }
  };

  if (loading || partsLoading || custLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (isNew) {
    return (
      <NewCheckoutForm
        parts={parts}
        customers={customers}
        onCancel={() => navigate("/equipment-checkout")}
        onCreated={(id) => navigate(`/equipment-checkout/${id}`)}
      />
    );
  }

  if (checkoutId && detail) {
    const c = detail.checkout;
    return (
      <div className="space-y-4">
        <Link to="/equipment-checkout" className="text-sm text-[#1e3a4f] hover:underline">
          ← All checkouts
        </Link>
        <PageHeader title={String(c.checkout_number)} subtitle={`Checked out to: ${String(c.checked_out_to)}`} />
        <div className="flex flex-wrap gap-4 text-sm">
          <StatusBadge status={String(c.status)} />
          {c.due_back_date ? <span>Due: {String(c.due_back_date)}</span> : null}
          {c.customer_name ? <span>Customer: {String(c.customer_name)}</span> : null}
        </div>
        <DataTable
          columns={[
            { key: "part", header: "Part", render: (l) => String(l.part_name) },
            { key: "qty", header: "Qty", className: "text-right", render: (l) => String(l.quantity) },
            { key: "ret", header: "Returned", className: "text-right", render: (l) => String(l.quantity_returned ?? 0) },
            {
              key: "action",
              header: "Return",
              render: (l) => {
                const remaining = Number(l.quantity) - Number(l.quantity_returned ?? 0);
                if (remaining <= 0) return "—";
                return (
                  <Button size="sm" variant="secondary" onClick={() => returnLine(String(l.id), remaining)}>
                    Return {remaining}
                  </Button>
                );
              },
            },
          ]}
          rows={detail.lines}
          rowKey={(l) => String(l.id)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Equipment Check-Out"
        actions={
          <Button asChild className="bg-[#1e3a4f] hover:bg-[#163041]">
            <Link to="/equipment-checkout?new=1">New Check-Out</Link>
          </Button>
        }
      />
      <DataTable
        columns={[
          {
            key: "num",
            header: "Checkout #",
            render: (c) => (
              <Link to={`/equipment-checkout/${c.id}`} className="font-medium text-[#1e3a4f] hover:underline">
                {c.checkout_number}
              </Link>
            ),
          },
          { key: "to", header: "Checked Out To", render: (c) => c.checked_out_to },
          { key: "customer", header: "Customer", render: (c) => c.customer_name || "—" },
          { key: "due", header: "Due Back", render: (c) => c.due_back_date || "—" },
          { key: "status", header: "Status", render: (c) => <StatusBadge status={c.status} /> },
        ]}
        rows={checkouts}
        rowKey={(c) => c.id}
      />
    </div>
  );
}

function NewCheckoutForm({
  parts,
  customers,
  onCancel,
  onCreated,
}: {
  parts: { id: string; part_name: string; on_hand: number }[];
  customers: { id: string; name: string }[];
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [checkedOutTo, setCheckedOutTo] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [dueBackDate, setDueBackDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ partId: "", quantity: 1, serialNotes: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const data = await apiFetch<{ id: string }>("/equipment-checkouts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checkedOutTo,
          customerId: customerId || undefined,
          dueBackDate: dueBackDate || undefined,
          notes,
          lines: lines.filter((l) => l.partId && l.quantity > 0),
        }),
      });
      onCreated(data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="New Equipment Check-Out" />
      <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Checked Out To</Label>
            <Input value={checkedOutTo} onChange={(e) => setCheckedOutTo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">None</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Due Back</Label>
            <Input type="date" value={dueBackDate} onChange={(e) => setDueBackDate(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Line Items</Label>
          {lines.map((line, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-3 items-end border-b pb-2">
              <Select value={line.partId} onChange={(e) => {
                const next = [...lines];
                next[i] = { ...next[i], partId: e.target.value };
                setLines(next);
              }}>
                <option value="">Select part…</option>
                {parts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.part_name} (on-hand: {p.on_hand})
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], quantity: Number(e.target.value) };
                  setLines(next);
                }}
              />
              <Input
                placeholder="Serial notes"
                value={line.serialNotes}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], serialNotes: e.target.value };
                  setLines(next);
                }}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setLines([...lines, { partId: "", quantity: 1, serialNotes: "" }])}
          >
            Add Line
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {submitting ? "Saving…" : "Create Check-Out"}
          </Button>
        </div>
      </div>
    </div>
  );
}
